/** Canonical COP thresholds for vehicle search budget and sidebar price filters. */

/** Shared tier breakpoints (quiz + matching). */
export const MARKET_BUDGET_TIER_DEFS = [
  { value: "0-45M", priceMin: 0, priceMax: 45_000_000 },
  { value: "45M-70M", priceMin: 45_000_000, priceMax: 70_000_000 },
  { value: "70M-100M", priceMin: 70_000_000, priceMax: 100_000_000 },
  { value: "100M-150M", priceMin: 100_000_000, priceMax: 150_000_000 },
  { value: "150M+", priceMin: 150_000_000, priceMax: null },
];

export const SEARCH_BUDGET_OPTION_DEFS = [
  { value: "all", priceMin: null, priceMax: null },
  { value: "0-45000000", priceMin: 0, priceMax: 45_000_000 },
  { value: "45000000-70000000", priceMin: 45_000_000, priceMax: 70_000_000 },
  { value: "70000000-100000000", priceMin: 70_000_000, priceMax: 100_000_000 },
  { value: "100000000-150000000", priceMin: 100_000_000, priceMax: 150_000_000 },
  { value: "150000000+", priceMin: 150_000_000, priceMax: null },
];

export const SEARCH_MIN_PRICE_THRESHOLDS_COP = [45_000_000, 60_000_000, 80_000_000, 100_000_000];

export const SEARCH_MAX_PRICE_THRESHOLDS_COP = [
  60_000_000,
  80_000_000,
  100_000_000,
  150_000_000,
  200_000_000,
];

export const SEARCH_MAX_PRICE_NO_LIMIT = 999_999_999;

/**
 * Compact COP budget label for quiz chips and tier pickers (e.g. 70M instead of 70,000,000).
 * @param {number|null|undefined} amountCop
 * @param {object} [options]
 * @param {string} [options.locale]
 */
export function formatCompactBudgetAmount(amountCop, { locale = "en-US" } = {}) {
  if (amountCop == null || Number.isNaN(Number(amountCop))) {
    return "";
  }

  const value = Number(amountCop);
  if (Math.abs(value) >= 1_000_000) {
    const millions = value / 1_000_000;
    const formatted =
      millions % 1 === 0
        ? millions.toLocaleString(locale, { maximumFractionDigits: 0 })
        : millions.toLocaleString(locale, { maximumFractionDigits: 1 });
    return `${formatted}M`;
  }

  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
}

/**
 * @param {number} cop
 * @param {(amountCop: number) => string} formatAmount
 */
export function formatSearchPriceThresholdLabel(cop, formatAmount) {
  if (cop == null || Number.isNaN(Number(cop))) return "";
  return formatAmount(Number(cop));
}

/**
 * @param {object} options
 * @param {Function} options.t
 * @param {(amountCop: number) => string} options.formatAmount
 */
export function buildSearchBudgetOptions({ t, formatAmount }) {
  return SEARCH_BUDGET_OPTION_DEFS.map((entry) => {
    if (entry.value === "all") {
      return { ...entry, label: t("automotive.search.budgetAny") };
    }
    if (entry.priceMin === 0 && entry.priceMax != null) {
      return {
        ...entry,
        label: t("automotive.quiz.budgetUpTo", { amount: formatAmount(entry.priceMax) }),
      };
    }
    if (entry.priceMax == null) {
      return {
        ...entry,
        label: t("automotive.quiz.budgetOver", { amount: formatAmount(entry.priceMin) }),
      };
    }
    return {
      ...entry,
      label: t("automotive.quiz.budgetRange", {
        min: formatAmount(entry.priceMin),
        max: formatAmount(entry.priceMax),
      }),
    };
  });
}

/**
 * @param {object} options
 * @param {Function} options.t
 * @param {(amountCop: number) => string} options.formatAmount
 */
export function buildSearchMinPriceOptions({ t, formatAmount }) {
  return [
    { label: t("automotive.search.filterStackNoMin"), value: 0 },
    ...SEARCH_MIN_PRICE_THRESHOLDS_COP.map((cop) => ({
      label: formatSearchPriceThresholdLabel(cop, formatAmount),
      value: cop,
    })),
  ];
}

/**
 * @param {object} options
 * @param {Function} options.t
 * @param {(amountCop: number) => string} options.formatAmount
 */
export function buildSearchMaxPriceOptions({ t, formatAmount }) {
  return [
    { label: t("automotive.search.filterStackNoMax"), value: SEARCH_MAX_PRICE_NO_LIMIT },
    ...SEARCH_MAX_PRICE_THRESHOLDS_COP.map((cop) => ({
      label: formatSearchPriceThresholdLabel(cop, formatAmount),
      value: cop,
    })),
  ];
}

export function budgetValueFromPrices(priceMin, priceMax, options = SEARCH_BUDGET_OPTION_DEFS) {
  const min = priceMin != null && priceMin !== "" ? Number(priceMin) : null;
  const max = priceMax != null && priceMax !== "" ? Number(priceMax) : null;
  const match = options.find((option) => {
    if (option.value === "all") return min == null && max == null;
    if (option.priceMin != null && min !== option.priceMin) return false;
    if (option.priceMax != null && max !== option.priceMax) return false;
    if (option.priceMax == null && max != null && max < SEARCH_MAX_PRICE_NO_LIMIT) return false;
    return true;
  });
  return match?.value || "all";
}

export function pricesFromBudgetValue(budgetValue, options = SEARCH_BUDGET_OPTION_DEFS) {
  const option = options.find((entry) => entry.value === budgetValue);
  if (!option || option.value === "all") {
    return { priceMin: null, priceMax: null };
  }
  return { priceMin: option.priceMin, priceMax: option.priceMax };
}
